import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData } from '../../services/request'

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')

  const fetchCompanies = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/companies`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setCompanies(Array.isArray(data) ? data : [])
      } else {
        setCompanies([])
        setError(res.result?.data?.message || res.result?.data?.data || 'Unable to load companies')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchCompanies()
  }, [])

  const ownerName = (c) => {
    const o = c.ownerUserId
    if (!o) return '—'
    return `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email || '—'
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return companies
    return companies.filter((c) => {
      const owner = c.ownerUserId
      const ownerEmail = owner?.email || ''
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.industry || '').toLowerCase().includes(q) ||
        (c.status || '').toLowerCase().includes(q) ||
        (c.size || '').toLowerCase().includes(q) ||
        ownerName(c).toLowerCase().includes(q) ||
        ownerEmail.toLowerCase().includes(q)
      )
    })
  }, [companies, query])

  const handleUpdateStatus = async (company, newStatus) => {
    const url = `${getAPIBaseURL()}/employme/admin/companies/${company._id}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: newStatus })
    if (res.error === '' && res.result?.status === 200) {
      setCompanies((prev) => prev.map((c) => (c._id === company._id ? { ...c, status: newStatus } : c)))
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update status')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="text-sm text-gray-600">View and verify employer companies</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search companies (name, industry, owner)"
            className="w-72 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
          <Link
            to="/admin/app/employme"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Back to overview
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            {query.trim() ? 'No companies match your search' : 'No companies found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Industry</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Owner</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Created</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/admin/app/employme/companies/${c._id}`} className="font-medium text-gray-900 hover:underline">
                        {c.name}
                      </Link>
                      <div className="text-xs text-gray-500">{c.size} employees</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{c.industry || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{ownerName(c)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          c.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-700'
                            : c.status === 'PENDING_VERIFICATION'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {c.status === 'PENDING_VERIFICATION' && (
                          <button
                            onClick={() => handleUpdateStatus(c, 'ACTIVE')}
                            className="rounded-lg border border-green-600 px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50"
                          >
                            Verify
                          </button>
                        )}
                        {c.status === 'ACTIVE' && (
                          <button
                            onClick={() => handleUpdateStatus(c, 'SUSPENDED')}
                            className="rounded-lg border border-red-600 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                          >
                            Suspend
                          </button>
                        )}
                        {c.status === 'SUSPENDED' && (
                          <button
                            onClick={() => handleUpdateStatus(c, 'ACTIVE')}
                            className="rounded-lg border border-green-600 px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminCompanies
